import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useI18n } from '../../context/I18nContext';
import { calculateCableQuantities, calculateTerminalQuantities, calculateSiglatureAndMorsetti } from '../../utils/quantities';
import { renderTerminalIconSimple } from '../../utils/terminalRenderLogic';
import { getCableColorForSVG, getTerminalData, hasCustomTerminalGraphic } from '../../utils/cableLogic';
import { exportSiglature } from '../../utils/export';
import { Icons } from '../icons/Icons';

// Componente per il rettangolo colore cavi
const CableColorRect = ({ color, width = "20px", height = "20px", borderRadius = "4px", border = "1px solid #ddd" }) => (
  <div
    style={{
      width,
      height,
      backgroundColor: color,
      borderRadius,
      border,
      flexShrink: 0
    }}
  />
);

const QuantitiesForm = ({ cables, settings, terminali = {}, setWarnings, setSuccessMessage }) => {
  const { t, language } = useI18n();
  const [showCableQuantities, setShowCableQuantities] = useState(false);
  const [showTerminalQuantities, setShowTerminalQuantities] = useState(false);
  const [showSiglature, setShowSiglature] = useState(false);
  const [siglaturaData, setSiglaturaData] = useState(null);

  // Calcoli memoizzati
  const quantities = useMemo(() => calculateCableQuantities(cables, settings), [cables, settings]);
  const terminalQuantities = useMemo(() => (calculateTerminalQuantities(cables, settings, terminali)), [cables, settings, terminali]);

  const calculateAllSiglature = useCallback(() => {
    const allResults = calculateSiglatureAndMorsetti(cables, settings);

    const total25_6 = allResults.section25_6.reduce((sum, item) => sum + item.quantita, 0);
    const total05_15 = allResults.section05_15.reduce((sum, item) => sum + item.quantita, 0);
    const totalAbove6 = allResults.sectionAbove6.reduce((sum, item) => sum + item.quantita, 0);
    const totalMorsetti = allResults.morsetti.reduce((sum, item) => sum + item.quantita, 0);

    return {
      section25_6: allResults.section25_6,
      section05_15: allResults.section05_15,
      sectionAbove6: allResults.sectionAbove6,
      morsetti: allResults.morsetti,
      total25_6,
      total05_15,
      totalAbove6,
      totalMorsetti,
      totalOverall: total25_6 + total05_15 + totalAbove6 + totalMorsetti
    };
  }, [cables, settings]);

  // Ricalcola siglature quando cambiano i cavi
  useEffect(() => {
    if (cables.length > 0) {
      setSiglaturaData(calculateAllSiglature());
    } else {
      setSiglaturaData(null);
    }
  }, [cables, calculateAllSiglature]);

  // Ricalcola siglature quando cambiano i cavi
  useEffect(() => {
    if (cables.length > 0) {
      setSiglaturaData(calculateAllSiglature());
    } else {
      setSiglaturaData(null);
    }
  }, [cables, calculateAllSiglature]);

  // Calcoli per i totali
  const totalCables = cables.length;
  const totalLength = cables.reduce((sum, cable) => sum + (cable.lunghezza || 0), 0);
  const realTotalCables = totalCables * settings.quantitaCablaggi;
  const realTotalLength = totalLength * settings.quantitaCablaggi;

  const totalTerminals = terminalQuantities.reduce((sum, item) => sum + item.count, 0);
  const realTotalTerminals = totalTerminals * settings.quantitaCablaggi;

  return (
    <div className="space-y-6">
      {/* TITOLO PRINCIPALE */}
      <h2 className="text-xl font-semibold mb-4 text-gray-800">{t('quantities.summaryTitle')}</h2>

      {/* COMPONENTE QUANTITÀ CAVI */}
      <div className="bg-green-50 rounded-lg p-6">
        <div
          className="flex justify-between items-center cursor-pointer"
          onClick={() => setShowCableQuantities(!showCableQuantities)}
        >
          <h3 className="text-lg font-semibold text-green-800 flex items-center gap-3">
            <Icons.FileSpreadsheet className="w-5 h-5" />
            {t('quantities.cableQuantities')}

            {/* BADGE QUANTITÀ CABLAGGI - PIÙ PROMINENTE */}
            <div className={`px-3 py-1 rounded-full text-sm font-bold ${settings.quantitaCablaggi > 1
              ? 'bg-orange-500 text-white shadow-md'
              : 'bg-gray-200 text-gray-700'
              }`}>
              {settings.quantitaCablaggi > 1 ? `×${settings.quantitaCablaggi} ${t('quantities.cablaggi')}` : '×1'}
            </div>
          </h3>
          <button className="text-green-600 hover:text-green-800 transition-colors text-lg">
            {showCableQuantities ? '▼' : '▷'}
          </button>
        </div>

        {showCableQuantities && (
          <>
            {/* AVVISO PROMINENTE se quantità > 1 */}
            {settings.quantitaCablaggi > 1 && (
              <div className="bg-orange-100 border border-orange-300 rounded-lg p-3 mb-4 mt-4">
                <div className="flex items-center gap-2">
                  <span className="text-orange-600 text-xl">⚠️</span>
                  <div>
                    <div className="text-sm font-bold text-orange-800">
                      Produzione Multiple: {settings.quantitaCablaggi} cablaggi identici
                    </div>
                    <div className="text-xs text-orange-700 mt-1">
                      I totali reali sono moltiplicati per {settings.quantitaCablaggi}. Verifica le quantità prima dell'ordine materiali.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tabella con una sola struttura e header sticky */}
            <div className="bg-white rounded-md mt-4 h-80 overflow-hidden">
              <div className="h-full overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50 border-b z-10">
                    <tr>
                      <th className="text-left py-2 pl-4">{t('quantities.cableType')}</th>
                      <th className="text-center py-2">{t('quantities.quantity')}</th>
                      <th className="text-center py-2">{t('quantities.totalLength')}</th>
                      {settings.quantitaCablaggi > 1 && (
                        <>
                          <th className="text-center py-2 text-orange-700">{t('quantities.realTotal')}</th>
                          <th className="text-center py-2 text-orange-700 pr-4">{t('quantities.realLength')}</th>
                        </>
                      )}
                      {settings.quantitaCablaggi === 1 && (
                        <th className="pr-4"></th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {quantities.map((item, index) => {
                      return (
                        <tr key={index} className="border-b hover:bg-gray-50">
                          <td className="py-3 pl-4 flex items-center gap-3">
                            {item.isMultipolar ? (
                              <>
                                <div className="w-5 h-5 bg-purple-500 rounded flex items-center justify-center text-white text-xs font-bold">M</div>
                                {item.tipo || item.colore}
                              </>
                            ) : (
                              <>
                                <CableColorRect
                                  color={getCableColorForSVG(item.colore)}
                                  width="20px"
                                  height="20px"
                                  borderRadius="4px"
                                  border="1px solid #ddd"
                                />
                                {settings?.useAWG ? `${item.colore} AWG${item.sezione}` : `${item.colore} ${item.sezione}mm²`}
                              </>
                            )}
                          </td>
                          <td className="text-center py-3">{item.count}</td>
                          <td className="text-center py-3">
                            {(item.totalLength / 1000).toFixed(2)}m
                          </td>
                          {settings.quantitaCablaggi > 1 && (
                            <>
                              <td className="text-center py-3 text-orange-700 font-bold">
                                {item.count * settings.quantitaCablaggi}
                              </td>
                              <td className="text-center py-3 text-orange-700 font-bold pr-4">
                                {((item.totalLength * settings.quantitaCablaggi) / 1000).toFixed(2)}m
                              </td>
                            </>
                          )}
                          {settings.quantitaCablaggi === 1 && (
                            <td className="pr-4"></td>
                          )}
                        </tr>
                      );
                    })}

                    {/* RIGA TOTALI - sempre in fondo */}
                    <tr className="border-t-2 border-green-500 bg-green-100 font-bold sticky bottom-0">
                      <td className="py-4 pl-4">
                        <div className="flex items-center gap-3">
                          <span className="text-green-800">{t('quantities.total')} ({quantities.length} {t('quantities.varieties')})</span>
                        </div>
                      </td>
                      <td className="text-center py-4 text-green-800">{totalCables}</td>
                      <td className="text-center py-4 text-green-800">
                        {(totalLength / 1000).toFixed(2)}m
                      </td>
                      {settings.quantitaCablaggi > 1 && (
                        <>
                          <td className="text-center py-4 text-orange-700 font-bold">
                            {realTotalCables}
                          </td>
                          <td className="text-center py-4 text-orange-700 font-bold pr-4">
                            {(realTotalLength / 1000).toFixed(2)}m
                          </td>
                        </>
                      )}
                      {settings.quantitaCablaggi === 1 && (
                        <td className="pr-4"></td>
                      )}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* COMPONENTE QUANTITÀ TERMINALI */}
      <div className="bg-indigo-50 rounded-lg p-6">
        <div
          className="flex justify-between items-center cursor-pointer"
          onClick={() => setShowTerminalQuantities(!showTerminalQuantities)}
        >
          <h3 className="text-lg font-semibold text-indigo-800 flex items-center gap-3">
            <Icons.Wire className="w-5 h-5" />
            {t('quantities.terminalQuantities')}

            {/* BADGE QUANTITÀ CABLAGGI - PIÙ PROMINENTE */}
            <div className={`px-3 py-1 rounded-full text-sm font-bold ${settings.quantitaCablaggi > 1
              ? 'bg-orange-500 text-white shadow-md'
              : 'bg-gray-200 text-gray-700'
              }`}>
              {settings.quantitaCablaggi > 1 ? `×${settings.quantitaCablaggi} ${t('quantities.cablaggi')}` : '×1'}
            </div>
          </h3>
          <button className="text-indigo-600 hover:text-indigo-800 transition-colors text-lg">
            {showTerminalQuantities ? '▼' : '▷'}
          </button>
        </div>

        {showTerminalQuantities && (
          <>
            {/* AVVISO PROMINENTE se quantità > 1 */}
            {settings.quantitaCablaggi > 1 && (
              <div className="bg-orange-100 border border-orange-300 rounded-lg p-3 mb-4 mt-4">
                <div className="flex items-center gap-2">
                  <span className="text-orange-600 text-xl">⚠️</span>
                  <div>
                    <div className="text-sm font-bold text-orange-800">
                      Produzione Multiple: {settings.quantitaCablaggi} cablaggi identici
                    </div>
                    <div className="text-xs text-orange-700 mt-1">
                      I totali reali sono moltiplicati per {settings.quantitaCablaggi}. Verifica le quantità prima dell'ordine materiali.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tabella terminali */}
            <div className="bg-white rounded-md mt-4 h-80 overflow-hidden">
              <div className="h-full overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50 border-b z-10">
                    <tr>
                      <th className="text-left py-2 pl-4">{t('quantities.terminalType')}</th>
                      <th className="text-center py-2">{t('quantities.quantity')}</th>
                      {settings.quantitaCablaggi > 1 && (
                        <th className="text-center py-2 text-orange-700 pr-4">{t('quantities.realTotal')}</th>
                      )}
                      {settings.quantitaCablaggi === 1 && (
                        <th className="pr-4"></th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {terminalQuantities.map((item, index) => {
                      const tData = getTerminalData(item.terminal, { ...settings, lingua: language }, terminali);
                      const hasCustomGraphic = hasCustomTerminalGraphic(item.terminal);

                      return (
                        <tr key={index} className="border-b hover:bg-gray-50">
                          <td className="py-3 pl-4 flex items-center gap-4">
                            {/* ICONA INGRANDITA */}
                            <div className="flex items-center justify-center flex-shrink-0">
                              {hasCustomGraphic ? (
                                renderTerminalIconSimple(item.terminal, 56, 28)
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-indigo-500 border-2 border-indigo-600 flex items-center justify-center">
                                  <span className="text-white text-sm font-bold">
                                    {item.terminal.charAt(0)}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* CODICE E DESCRIZIONE COMBINATI */}
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-gray-900">
                                {item.terminal}
                              </div>
                              <div className="text-xs text-gray-600 truncate">
                                {tData?.nome || t('quantities.terminal')}
                              </div>
                            </div>
                          </td>
                          <td className="text-center py-3 font-semibold">{item.count}</td>
                          {settings.quantitaCablaggi > 1 && (
                            <td className="text-center py-3 text-orange-700 font-bold pr-4">
                              {item.count * settings.quantitaCablaggi}
                            </td>
                          )}
                          {settings.quantitaCablaggi === 1 && (
                            <td className="pr-4"></td>
                          )}
                        </tr>
                      );
                    })}
                    {/* RIGA TOTALI - sempre in fondo */}
                    <tr className="border-t-2 border-indigo-500 bg-indigo-100 font-bold sticky bottom-0">
                      <td className="py-4 pl-4">
                        <div className="flex items-center gap-3">
                          <span className="text-indigo-800">{t('quantities.total')} ({terminalQuantities.length} {t('quantities.varieties')})</span>
                        </div>
                      </td>
                      <td className="text-center py-4 text-indigo-800">{totalTerminals}</td>
                      {settings.quantitaCablaggi > 1 && (
                        <td className="text-center py-4 text-orange-700 font-bold pr-4">
                          {realTotalTerminals}
                        </td>
                      )}
                      {settings.quantitaCablaggi === 1 && (
                        <td className="pr-4"></td>
                      )}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* COMPONENTE SIGLATURE */}
      <div className="bg-purple-50 rounded-lg p-6">
        <div
          className="flex justify-between items-center cursor-pointer"
          onClick={() => setShowSiglature(!showSiglature)}
        >
          <h3 className="text-lg font-semibold text-purple-800 flex items-center gap-3">
            <Icons.FileSpreadsheet className="w-5 h-5" />
            {t('quantities.signatureCalculation')}

            {siglaturaData && (
              <div className="px-3 py-1 rounded-full text-sm font-bold bg-purple-500 text-white shadow-md">
                {siglaturaData.totalOverall} {t('quantities.totalLabel')}
              </div>
            )}
          </h3>
          <button className="text-purple-600 hover:text-purple-800 transition-colors text-lg">
            {showSiglature ? '▼' : '▷'}
          </button>
        </div>

        {showSiglature && (
          <>
            {cables.length === 0 ? (
              <div className="text-center py-8 text-purple-600">
                <Icons.FileSpreadsheet className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>{t('quantities.noCablesInserted')}</p>
                <p className="text-xs mt-1">{t('quantities.addCablesToCalculate')}</p>
              </div>
            ) : !siglaturaData ? (
              <div className="text-center py-4 text-purple-600">
                <p>{t('quantities.loadingData')}</p>
              </div>
            ) : (
              <>
                {/* Summary totali - MOSTRA SEMPRE i morsetti */}
                <div className="bg-white rounded-md p-4 mt-4 mb-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-bold text-purple-800">{t('quantities.summaryTotals')}</h4>
                    <button
                      onClick={() => exportSiglature(cables, settings, setWarnings, setSuccessMessage)}
                      disabled={!siglaturaData}
                      className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 disabled:bg-gray-300 transition-colors flex items-center gap-2"
                    >
                      <Icons.Download className="w-4 h-4" />
                      {t('quantities.exportSignatures')}
                    </button>
                  </div>

                  <div className="grid grid-cols-5 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-green-600">
                        {siglaturaData.total05_15}
                      </div>
                      <div className="text-sm text-gray-600">{t('quantities.signatures05_15')}</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-blue-600">
                        {siglaturaData.total25_6}
                      </div>
                      <div className="text-sm text-gray-600">{t('quantities.signatures25_6')}</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-red-600">
                        {siglaturaData.totalAbove6}
                      </div>
                      <div className="text-sm text-gray-600">{t('quantities.flatTags')}</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-orange-600">
                        {siglaturaData.totalMorsetti}
                      </div>
                      <div className="text-sm text-gray-600">{t('quantities.terminals')}</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-purple-600">
                        {siglaturaData.totalOverall}
                      </div>
                      <div className="text-sm text-gray-600">{t('quantities.totalOverall')}</div>
                    </div>
                  </div>
                </div>

                {/* Sezioni tabelle - INCLUDE SEMPRE i morsetti */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    siglaturaData.total05_15 > 0 && {
                      key: 'section05_15',
                      title: t('quantities.signatures05_15'),
                      color: 'green',
                      data: siglaturaData.section05_15
                    },
                    siglaturaData.total25_6 > 0 && {
                      key: 'section25_6',
                      title: t('quantities.signatures25_6'),
                      color: 'blue',
                      data: siglaturaData.section25_6
                    },
                    siglaturaData.totalAbove6 > 0 && {
                      key: 'sectionAbove6',
                      title: t('quantities.flatTags'),
                      color: 'red',
                      data: siglaturaData.sectionAbove6
                    },
                    siglaturaData.totalMorsetti > 0 && {
                      key: 'morsetti',
                      title: t('quantities.terminals'),
                      color: 'orange',
                      data: siglaturaData.morsetti
                    }
                  ]
                    .filter(Boolean)
                    .map(section => (
                      <div key={section.key} className="bg-white rounded-md overflow-hidden">
                        <h5 className={`font-bold p-4 pb-3 text-${section.color}-600`}>
                          📋 {section.title}
                        </h5>

                        {/* Tabella con colonne raggruppate */}
                        <div className="bg-white rounded-md h-80 overflow-hidden">
                          <div className="h-full overflow-y-auto">
                            <table className="w-full text-sm table-fixed">
                              {/* Definizione larghezze colonne - RAGGRUPPATE */}
                              <colgroup>
                                <col className="w-[30%]" />
                                <col className="w-[10%]" />
                                <col className="w-[30%]" />
                                <col className="w-[30%]" />
                              </colgroup>

                              <thead className="sticky top-0 bg-gray-50 border-b z-10">
                                <tr>
                                  <th className="text-center py-2 font-medium text-gray-700">{t('quantities.start')}</th>
                                  <th className="text-center py-2 font-medium text-gray-700">{t('quantities.repeat')}</th>
                                  <th className="text-center py-2 font-medium text-gray-700">{t('quantities.end')}</th>
                                  <th className="text-center py-2 font-medium text-gray-700 border-l border-gray-200">{t('quantities.quantity')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {section.data.map((item, idx) => (
                                  <tr key={idx} className="border-b hover:bg-gray-50">
                                    <td className="text-center py-3 font-medium truncate" title={item.inizio}>
                                      {item.inizio}
                                    </td>
                                    <td className="text-center py-3">
                                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded font-bold text-xs">
                                        {item.ripetizioni}
                                      </span>
                                    </td>
                                    <td className="text-center py-3 font-medium">
                                      {item.fine || '—'}
                                    </td>
                                    <td className="text-center py-3 border-l border-gray-200">
                                      <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded font-bold text-xs">
                                        {item.quantita}
                                      </span>
                                    </td>
                                  </tr>
                                ))}

                                {/* FOOTER STICKY */}
                                <tr className={`border-t-2 border-${section.color}-500 bg-${section.color}-100 font-bold sticky bottom-0`}>
                                  <td className="py-4 pl-8 text-left">
                                    <span className={`text-${section.color}-800`}>{t('quantities.total')}</span>
                                  </td>
                                  <td className="py-4"></td>
                                  <td className="py-4"></td>
                                  <td className="text-center py-4 border-l border-gray-200">
                                    <span className={`bg-${section.color}-500 text-white px-2 py-1 rounded font-bold text-xs`}>
                                      {section.data.reduce((sum, item) => sum + item.quantita, 0)}
                                    </span>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default QuantitiesForm;